import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ItemProcess as View } from '@views';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { routes } from '@constants';
import { formatPrice as formatDiscountAmount } from '@helpers/menuItemDetails';
import {
  getItemSizes, getItemSpecials, isPriceValid, formatPrice,
} from '@helpers/itemEdit';
import { isClientError } from '@helpers/errorHandling';
import { getEntityAvailabilities, getNumberFromString, hasSizesWithNullablePrice } from '@helpers/common';
import {
  deleteItem, createItem, editItem, detachAvailabilityFromItem, editAvailability, deleteAvailability,
  editSpecialSize, createSpecialSize, deleteSpecialSize, detachAvailabilityFromSpecialSize,
} from '@store/menu/duck';

const ItemProcess = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    categories, subcategories, items, sizes, deleteItemPhase, editItemPhase, createItemPhase, availabilities,
    detachAvailabilityFromItemPhase, detachAvailabilityFromItemError, deleteAvailabilityPhase,
    editAvailabilityPhase, specialsSize, createSpecialSizePhase, editSpecialSizePhase, deleteSpecialSizePhase,
    detachAvailabilityFromSpecialSizePhase, detachAvailabilityFromSpecialSizeError,
  } = useSelector(state => state.menu);

  const itemId = location.state ? location.state.id : 0;

  const memoizedSizes = useMemo(() => getItemSizes(itemId, items, sizes), [itemId]);
  const initialDescription = itemId && items[itemId]
    ? items[itemId].description
    : '';
  const initialActivity = itemId && items[itemId]
    ? items[itemId].active
    : true;
  const initialName = itemId && items[itemId] ? items[itemId].name : '';
  const initialImage = itemId && items[itemId] && items[itemId].image[0]
    ? items[itemId].image[0].src
    : '';
  const initialSizes = itemId && items[itemId] ? items[itemId].sizeIds : [];
  const initialSubcategory = location.state
    ? categories.byId[location.state.categoryId].subcategoryIds[0]
    : 0;
  const availableSubcategories = location.state
    ? categories.byId[location.state.categoryId].subcategoryIds.map(id => ({
      name: subcategories[id].name,
      id,
    }))
    : [];
  const memoizedAvailabilities = useMemo(() => (
    getEntityAvailabilities(items[itemId]?.availabilityIds, availabilities)
  ), [availabilities, items]);

  // <-------- Item ---------> //
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(initialSubcategory);
  const [isItemActive, setIsItemActive] = useState(initialActivity);
  const [itemSizes, setItemSizes] = useState(memoizedSizes);
  const [imageFile, setImageFile] = useState(null);
  const [image, setImage] = useState(initialImage);
  const [itemName, setItemName] = useState(initialName);
  const [itemPrice, setItemPrice] = useState(0);
  const [itemDescription, setItemDescription] = useState(initialDescription);
  const [hasServerError, setHasServerError] = useState(false);
  const [error, setError] = useState(null);

  const sizesOrder = itemSizes.map(size => size.id);

  // Check if something has changed (we don't need to edit item if info is the same)
  const isDataTheSame = (!!itemId && !!items[itemId]
  && (image === initialImage)
  && (isItemActive === initialActivity)
  && itemDescription === initialDescription
  && itemName === initialName
  && JSON.stringify(sizesOrder) === JSON.stringify(initialSizes));

  // Check if item name !== '', if price !== 0,
  const isConfirmButtonDisabled = !itemName;

  // <-------- Item availability ---------> //
  const [availabilityList, setAvailabilityList] = useState(memoizedAvailabilities);
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [availabilityId, setAvailabilityId] = useState(null);
  const [availabilityName, setAvailabilityName] = useState('');
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isAvailabilityDeleteModalOpen, setIsAvailabilityDeleteModalOpen] = useState(false);
  const [isSpecialDeleteModalOpen, setIsSpecialDeleteModalOpen] = useState(false);

  // Check if something has changed (we don't need to edit availability if info is the same)
  const isAvailabilityDataTheSame = (!!availabilityId && !!availabilities[availabilityId]
    && startDate
    && ((!endDate && !availabilities[availabilityId].endDate) || endDate)
    && startTime
    && endTime
    && (availabilities[availabilityId].name === availabilityName)
    && (availabilities[availabilityId].startDate === startDate.toISOString())
    && availabilities[availabilityId].startTime === startTime.toISOString()
    && ((availabilities[availabilityId].endDate && availabilities[availabilityId].endDate === endDate.toISOString())
    || (!availabilities[availabilityId].endDate && !endDate))
    && availabilities[availabilityId].endTime === endTime.toISOString()
    && JSON.stringify(selectedWeekDays) === JSON.stringify(availabilities[availabilityId].weekDays));

  const isAvailabilityConfirmButtonDisabled = !availabilityName
  || !selectedWeekDays.length
  || !startTime
  || !endTime
  || !startDate
  || new Date(startTime.setDate(1)).toISOString() > new Date(endTime.setDate(1)).toISOString()
  // set date to have the same date and compare only time
  || (!!endDate && new Date(startDate).toISOString() > new Date(endDate).toISOString());

  // <-------- Item specials --------->//

  const memoizedSpecials = useMemo(() => (
    getItemSpecials(specialsSize, items, itemId, sizes)
  ), [specialsSize, items, itemId, sizes]);

  const [specialId, setSpecialId] = useState(null);
  const [specials, setSpecials] = useState(memoizedSpecials);
  const [isSizesSpecialModalOpen, setIsSizesSpecialModalOpen] = useState(false);
  const [isSpecialModalOpen, setIsSpecialModalOpen] = useState(false);
  const [discountType, setDiscountType] = useState('price');
  const [price, setPrice] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [discountPercent, setDiscountPercent] = useState('0');
  const [selectedSizeSpecial, setSelectedSizeSpecial] = useState(null);
  const [isSpecialWarningModalOpen, setIsSpecialWarningModalOpen] = useState(false);

  const memoizedSpecialAvailabilities = useMemo(() => (
    getEntityAvailabilities(specialsSize[specialId]?.availabilityIds, availabilities)
  ), [availabilities, specialsSize, specialId]);

  const handleSpecialModalChangeState = () => {
    setSpecialId(null);
    setPrice(0);
    setDiscountAmount(0);
    setDiscountPercent(0);
    setDiscountType('price');
    setIsSpecialModalOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (deleteItemPhase === SUCCESS
      || createItemPhase === SUCCESS
      || editItemPhase === SUCCESS) {
      history.push({
        pathname: routes.menuItems,
        state: {
          selectedCategoryId: location.state.categoryId,
        },
      });
    }
    if (deleteItemPhase === FAILURE
      || createItemPhase === FAILURE
      || editItemPhase === FAILURE) {
      setHasServerError(true);
    }
  }, [deleteItemPhase, editItemPhase, createItemPhase]);

  useEffect(() => {
    if (detachAvailabilityFromItemPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(items[itemId]?.availabilityIds, availabilities));
    }
    if (detachAvailabilityFromItemPhase === FAILURE) {
      if (isClientError(detachAvailabilityFromItemError && detachAvailabilityFromItemError.data.status)) {
        setError(detachAvailabilityFromItemError.data.error);
      }
    }
  }, [detachAvailabilityFromItemPhase]);

  useEffect(() => {
    if (detachAvailabilityFromSpecialSizePhase === FAILURE) {
      if (isClientError(detachAvailabilityFromSpecialSizeError
         && detachAvailabilityFromSpecialSizeError.data.status)) {
        setError(detachAvailabilityFromSpecialSizeError.data.error);
      }
    }
  }, [detachAvailabilityFromSpecialSizePhase]);

  useEffect(() => {
    if (editAvailabilityPhase === SUCCESS
      || deleteAvailabilityPhase === SUCCESS) {
      setAvailabilityList(getEntityAvailabilities(items[itemId]?.availabilityIds, availabilities));
      setIsAvailabilityDeleteModalOpen(false);
      setIsAvailabilityModalOpen(false);
    }
    if (editAvailabilityPhase === FAILURE
      || deleteAvailabilityPhase === FAILURE) {
      setIsAvailabilityModalOpen(false);
    }
  }, [deleteAvailabilityPhase, editAvailabilityPhase]);

  useEffect(() => {
    if (editSpecialSizePhase === SUCCESS
      || createSpecialSizePhase === SUCCESS
      || deleteSpecialSizePhase === SUCCESS) {
      setSpecials(getItemSpecials(specialsSize, items, itemId, sizes));
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
    if (editSpecialSizePhase === FAILURE
      || createSpecialSizePhase === FAILURE
      || deleteSpecialSizePhase === FAILURE) {
      handleSpecialModalChangeState();
      setIsSpecialDeleteModalOpen(false);
      setIsSpecialWarningModalOpen(false);
    }
  }, [editSpecialSizePhase, createSpecialSizePhase, deleteSpecialSizePhase]);

  // <-------- Item actions --------->//

  const handleToastClose = () => {
    setHasServerError(false);
  };

  const handleSelectedSubcategoryChange = subcategoryId => () => {
    setSelectedSubcategory(subcategoryId);
  };

  const handleItemDelete = () => {
    dispatch(deleteItem({
      id: parseInt(itemId, 10),
    }));
  };

  const handleItemPriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered price !== 0 to not replace whole value with empty string
      setItemPrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(itemPrice, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDragSizeEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedSizes = [...itemSizes];
      reorderedSizes.splice(source.index, 1);
      reorderedSizes.splice(destination.index, 0, itemSizes[source.index]);
      setItemSizes(reorderedSizes);
    }
  };

  const handleModalChangeState = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleWarningChangeState = () => {
    setIsWarningOpen(prevState => !prevState);
  };

  const handleItemActivityChange = () => {
    setIsItemActive(prevState => !prevState);
  };

  const handleEditCancel = () => {
    history.push({
      pathname: routes.menuItems,
      state: {
        selectedCategoryId: location.state.categoryId,
      },
    });
  };

  const handleItemNameChange = e => {
    setItemName(e.target.value);
  };

  const handleImageRemove = () => {
    setImage('');
  };

  const handleImageChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setImage(URL.createObjectURL(e.target.files[0]));
      setImageFile(e.target.files[0]);
    }
  };

  const handleSizeAdd = () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.sizeEdit,
        state: {
          itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else {
      setIsWarningOpen(true);
    }
  };

  const handleEditSize = sizeId => () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.sizeEdit,
        state: {
          sizeId,
          itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else {
      setIsWarningOpen(true);
    }
  };

  const handleItemDescriptionChange = e => {
    setItemDescription(e.target.value);
  };

  const handleEditSubmit = () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.menuItems,
        state: {
          selectedCategoryId: location.state.categoryId,
        },
      });
    } else {
      const itemData = new FormData();

      if ((!itemId && image) || (itemId && image !== initialImage)) {
        itemData.append('image', imageFile);
      }

      if (!itemId || (itemId && itemName !== items[itemId].name)) {
        itemData.append('name', itemName);
      }

      if ((!itemId && itemDescription) || (itemId && itemDescription !== items[itemId].description)) {
        itemData.append('description', itemDescription);
      }

      if (itemId && (JSON.stringify(sizesOrder) !== JSON.stringify(items[itemId].sizeIds))) {
        itemData.append('sizesOrder', JSON.stringify(sizesOrder));
      }

      if (!itemId || (itemId && isItemActive !== items[itemId].isActive)) {
        itemData.append('active', isItemActive);
      }

      if (itemId) {
        itemData.append('id', itemId);

        dispatch(editItem(
          itemData,
        ));
      } else {
        itemData.append('price', parseInt(itemPrice * 100, 10)); // Convert dolars to cents
        itemData.append('categoryId', location.state.categoryId);

        if (selectedSubcategory) {
          itemData.append('subcategoryId', selectedSubcategory);
        }
        dispatch(createItem(
          itemData,
        ));
      }
    }
  };

  // <-------- Item special actions --------->//

  const handleDiscountTypeChange = typeId => () => {
    setDiscountType(typeId);
  };

  const handlePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered price !== 0 to not replace whole value with empty string
      setPrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(price, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountAmountChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountAmount(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountAmount, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleDiscountPercentChange = e => {
    let cash;
    if (!e.target.value.includes('%')) {
      cash = e.target.value.replace(',', '.').slice(0, -1);
    } else {
      cash = e.target.value.replace(',', '.').replace('%', '');
    }
    if (isPriceValid(cash)) { // we check if entered value !== 0 to not replace whole value with empty string
      setDiscountPercent(formatPrice(parseInt(cash, 10) !== 0 && parseInt(discountPercent, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleSpecialDeleteModalChangeState = () => {
    setIsSpecialDeleteModalOpen(prevState => !prevState);
  };

  const handleSpecialDelete = () => {
    dispatch(deleteSpecialSize({
      id: parseInt(specialId, 10),
    }));
  };

  const handleSelectedSizeSpecialChange = sizeId => () => {
    setSelectedSizeSpecial(sizeId);
    setIsSizesSpecialModalOpen(false);
    setIsSpecialModalOpen(true);
  };

  const setDiscountValue = id => {
    if (specialsSize[id].discountType === 'price') {
      setPrice(formatDiscountAmount(specialsSize[id].discountValue));
    }
    if (specialsSize[id].discountType === 'discount_amount') {
      setDiscountAmount(formatDiscountAmount(specialsSize[id].discountValue));
    }
    if (specialsSize[id].discountType === 'discount_percent') {
      setDiscountPercent(formatDiscountAmount(specialsSize[id].discountValue));
    }
  };

  const handleSpecialEdit = id => () => {
    setSpecialId(id);
    setSelectedSizeSpecial(specialsSize[id].sizeId);
    setDiscountType(specialsSize[id].discountType);
    setDiscountValue(id);
    setIsSpecialModalOpen(prevState => !prevState);
  };

  const handleSizesSpecialModalChangeState = () => {
    setIsSizesSpecialModalOpen(prevState => !prevState);
  };

  const handleSpecialAdd = () => {
    setIsSizesSpecialModalOpen(true);
  };

  const discountTypes = {
    price: {
      name: 'PRICE',
      inputValue: `$${price}`,
      inputHandler: handlePriceChange,
    },
    discount_amount: {
      name: 'DISCOUNT AMOUNT',
      inputValue: `$${discountAmount}`,
      inputHandler: handleDiscountAmountChange,
    },
    discount_percent: {
      name: 'DISCOUNT PERCENT',
      inputValue: `${discountPercent}%`,
      inputHandler: handleDiscountPercentChange,
    },
  };

  const handleSpecialWarningModalClose = () => {
    setIsSpecialWarningModalOpen(false);
  };

  const isSpecialDataTheSame = (!!specialId && !!specialsSize[specialId]
    && (specialsSize[specialId].discountType === discountType)
    && (specialsSize[specialId].discountValue === getNumberFromString(discountTypes[discountType].inputValue))
  );

  const isSpecialConfirmButtonDisabled = !getNumberFromString(discountTypes[discountType].inputValue);

  const handleEditConfirm = () => {
    const specialSize = {
      discountType,
      discountValue: getNumberFromString(discountTypes[discountType].inputValue),
    };

    if (specialId) {
      specialSize.id = specialId;
      dispatch(editSpecialSize(specialSize));
    } else {
      specialSize.sizeId = selectedSizeSpecial;
      dispatch(createSpecialSize(specialSize));
    }
  };

  const handleSpecialEditSubmit = () => {
    if (isSpecialDataTheSame) {
      handleSpecialModalChangeState();
    } else if (hasSizesWithNullablePrice(memoizedSizes, sizes, selectedSizeSpecial,
      discountType, discountTypes[discountType].inputValue)) {
      setIsSpecialWarningModalOpen(true);
    } else {
      handleEditConfirm();
    }
  };

  const handleSpecialAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromSpecialSize({
      availabilityId: parseInt(id, 10),
      specialSizeId: parseInt(specialId, 10),
    }));
  };

  const handleSpecialAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: specialId,
        appliedIds: specialsSize[specialId]?.availabilityIds,
        entity: 'specialSize',
      },
    });
  };

  // <-------- Item availability actions --------->//

  const handleAvailabilityAdd = () => {
    history.push({
      pathname: routes.availabilitiesList,
      state: {
        id: itemId,
        entity: 'item',
        appliedIds: items[itemId]?.availabilityIds,
      },
    });
  };

  const handleAvailabilityDetach = id => () => {
    dispatch(detachAvailabilityFromItem({
      availabilityId: parseInt(id, 10),
      itemId: parseInt(itemId, 10),
    }));
  };

  const handleDayClick = dayId => () => {
    const days = [...selectedWeekDays];
    if (days.includes(dayId)) {
      const index = days.indexOf(dayId);

      if (index > -1) {
        days.splice(index, 1);
      }
    } else {
      days.push(dayId);
    }
    setSelectedWeekDays(days);
  };

  const handleAvailabilityDeleteModalChangeState = () => {
    setIsAvailabilityDeleteModalOpen(prevState => !prevState);
  };

  const handleAvailabilityNameChange = e => {
    setAvailabilityName(e.target.value);
  };

  const handleAvailabilityModalChangeState = () => {
    setAvailabilityId(null);
    setAvailabilityName('');
    setStartTime('');
    setEndTime('');
    setStartDate('');
    setEndDate('');
    setSelectedWeekDays([]);
    setIsAvailabilityModalOpen(prevState => !prevState);
  };

  const handleStartTimeChange = time => {
    setStartTime(time);
  };

  const handleEndTimeChange = time => {
    setEndTime(time);
  };

  const handleStartDateChange = date => {
    setStartDate(date);
  };

  const handleEndDateChange = date => {
    setEndDate(date);
  };

  const handleAvailabilityEdit = editingId => () => {
    setIsAvailabilityModalOpen(true);
    setAvailabilityId(editingId);
    setAvailabilityName(availabilities[editingId].name);
    setStartTime(new Date(availabilities[editingId].startTime));
    setEndTime(new Date(availabilities[editingId].endTime));
    setStartDate(new Date(availabilities[editingId].startDate));
    setEndDate(availabilities[editingId].endDate ? new Date(availabilities[editingId].endDate) : '');
    setSelectedWeekDays(availabilities[editingId].weekDays);
  };

  const handleAvailabilityDelete = () => {
    dispatch(deleteAvailability({
      id: parseInt(availabilityId, 10),
    }));
  };

  const handleAvailabilityEditSubmit = () => {
    if (isAvailabilityDataTheSame) {
      setIsAvailabilityModalOpen(false);
    } else {
      const availability = {};
      if ((availabilityName && !availabilityId)
        || (availabilityName && availabilityId && availabilityName !== availabilities[availabilityId].name)) {
        availability.name = availabilityName;
      }
      if ((selectedWeekDays.length && !availabilityId)
        || (selectedWeekDays.length && availabilityId
          && JSON.stringify(selectedWeekDays) !== JSON.stringify(availabilities[availabilityId].weekDays))) {
        availability.weekDays = selectedWeekDays;
      }
      if (startDate) {
        availability.startDate = startDate.toISOString();
      }
      if (endDate) {
        availability.endDate = endDate.toISOString();
      }
      if (!endDate && !!availabilities[availabilityId]?.endDate) {
        availability.endDate = null;
      }
      if (startTime) {
        availability.startTime = startTime.toISOString();
      }
      if (endTime) {
        availability.endTime = endTime.toISOString();
      }
      availability.id = parseInt(availabilityId, 10);

      dispatch(editAvailability(availability));
    }
  };

  const handleStartDateRemove = () => {
    setStartDate('');
  };

  const handleEndDateRemove = () => {
    setEndDate('');
  };

  const handleStartTimeRemove = () => {
    setStartTime('');
  };

  const handleEndTimeRemove = () => {
    setEndTime('');
  };

  return (
    <View
      // specials
      onSpecialWarningModalClose={handleSpecialWarningModalClose}
      onEditConfirm={handleEditConfirm}
      isSpecialWarningModalOpen={isSpecialWarningModalOpen}
      specialAvailabilities={memoizedSpecialAvailabilities}
      onSpecialAvailabilityAdd={handleSpecialAvailabilityAdd}
      onSpecialAvailabilityDetach={handleSpecialAvailabilityDetach}
      isSpecialConfirmButtonDisabled={isSpecialConfirmButtonDisabled}
      onSpecialDeleteModalChangeState={handleSpecialDeleteModalChangeState}
      onSpecialDelete={handleSpecialDelete}
      isSpecialDeleteModalOpen={isSpecialDeleteModalOpen}
      onSpecialEditSubmit={handleSpecialEditSubmit}
      onSpecialAdd={handleSpecialAdd}
      onSizesSpecialModalChangeState={handleSizesSpecialModalChangeState}
      isSizesSpecialModalOpen={isSizesSpecialModalOpen}
      selectedSizeSpecial={selectedSizeSpecial}
      onSelectedSizeSpecialChange={handleSelectedSizeSpecialChange}
      onSpecialEdit={handleSpecialEdit}
      isEditingSpecial={!!specialId}
      discountTypes={discountTypes}
      onSpecialModalChangeState={handleSpecialModalChangeState}
      isSpecialModalOpen={isSpecialModalOpen}
      onDiscountTypeChange={handleDiscountTypeChange}
      selectedDiscountType={discountType}
      itemSpecials={specials}
      // item availability
      onStartDateRemove={handleStartDateRemove}
      onEndDateRemove={handleEndDateRemove}
      onStartTimeRemove={handleStartTimeRemove}
      onEndTimeRemove={handleEndTimeRemove}
      onAvailabilityEdit={handleAvailabilityEdit}
      isAvailabilityConfirmButtonDisabled={isAvailabilityConfirmButtonDisabled}
      onAvailabilityModalChangeState={handleAvailabilityModalChangeState}
      isAvailabilityModalOpen={isAvailabilityModalOpen}
      onAvailabilityDelete={handleAvailabilityDelete}
      onAvailabilityDeleteModalChangeState={handleAvailabilityDeleteModalChangeState}
      onAvailabilityEditSubmit={handleAvailabilityEditSubmit}
      isAvailabilityDeleteModalOpen={isAvailabilityDeleteModalOpen}
      isAvailabilityEditing={!!availabilityId}
      startTime={startTime.toString()}
      endTime={endTime.toString()}
      startDate={startDate.toString()}
      endDate={endDate.toString()}
      onStartTimeChange={handleStartTimeChange}
      onEndTimeChange={handleEndTimeChange}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      onDayClick={handleDayClick}
      selectedWeekDays={selectedWeekDays}
      onAvailabilityNameChange={handleAvailabilityNameChange}
      availabilityName={availabilityName}
      onAvailabilityDetach={handleAvailabilityDetach}
      availabilities={availabilityList}
      onAvailabilityAdd={handleAvailabilityAdd}
      // item
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      isDataTheSame={isDataTheSame}
      isWarningOpen={isWarningOpen}
      onWarningChangeState={handleWarningChangeState}
      onEditSize={handleEditSize}
      selectedSubcategory={parseInt(selectedSubcategory, 10)}
      availableSubcategories={availableSubcategories}
      onSelectedSubcategoryChange={handleSelectedSubcategoryChange}
      itemPrice={itemPrice.toString()}
      onItemPriceChange={handleItemPriceChange}
      onDragSizeEnd={handleDragSizeEnd}
      isEditing={!!itemId}
      isModalOpen={isModalOpen}
      onItemDelete={handleItemDelete}
      onModalChangeState={handleModalChangeState}
      isItemActive={isItemActive}
      onItemActivityChange={handleItemActivityChange}
      onSizeAdd={handleSizeAdd}
      sizes={itemSizes}
      image={image}
      onImageRemove={handleImageRemove}
      onImageChange={handleImageChange}
      onItemDescriptionChange={handleItemDescriptionChange}
      itemDescription={itemDescription}
      itemName={itemName}
      onItemNameChange={handleItemNameChange}
      onEditSubmit={handleEditSubmit}
      onEditCancel={handleEditCancel}
      onToastClose={handleToastClose}
      hasServerError={hasServerError}
      error={error}
      isLoading={deleteItemPhase === LOADING
        || createItemPhase === LOADING
        || editItemPhase === LOADING
        || detachAvailabilityFromItemPhase === LOADING
        || deleteAvailabilityPhase === LOADING
        || editAvailabilityPhase === LOADING
        || createSpecialSizePhase === LOADING
        || editSpecialSizePhase === LOADING
        || deleteSpecialSizePhase === LOADING
        || detachAvailabilityFromSpecialSizePhase === LOADING}
    />
  );
};

export default ItemProcess;
